import React from 'react';
import PerformanceSummaryWidget from './PerformanceSummaryWidget';
import HighchartsReact from 'highcharts-react-official';
import { Common } from '../../utils/common';
import PropTypes from 'prop-types';
import _ from 'lodash';

const Highcharts = window.Highcharts;

const BLENDED_TICKET_VALUE = 3000;

function Dashboard({
  data,
  sixMonthAverageConversionIndustry,
  sixMonthAverageConversionYou,
  currentYear,
  previousYear,
}) {
  const [startYearData, endYearData] = _.partition(data.results, (item) =>
    item.start_date.startsWith(previousYear)
  );

  const latestMonthData = endYearData[endYearData.length - 1];

  const bookableProspectsStartData = startYearData.map((item) => item.bookable_calls);
  const bookableProspectsEndData = endYearData.map((item) => item.bookable_calls);

  const appointmentConversionStartData = startYearData.map((item) => item.appt_conversion_rate);
  const appointmentConversionEndData = endYearData.map((item) => item.appt_conversion_rate);

  const appointmentsSetStartData = startYearData.map((item) => item.appt_calls);
  const appointmentsSetEndData = endYearData.map((item) => item.appt_calls);

  const missedOpportunitiesStartData = startYearData.map((item) => item.noappt_calls);
  const missedOpportunitiesEndData = endYearData.map((item) => item.noappt_calls);

  function createSplineChartOptions(startData, endData, isPercentage = false) {
    return {
      chart: {
        type: 'spline',
        height: 350,
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
      },
      xAxis: {
        categories: [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ],
      },
      yAxis: {
        title: { text: undefined },
        labels: {
          format: isPercentage ? '{value}%' : undefined,
        },
      },
      series: [
        {
          name: previousYear,
          data: startData,
          dashStyle: 'dash',
          color: '#0369a1',
        },
        {
          name: currentYear,
          data: endData,
          color: '#991b1b',
        },
      ],
      exporting: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      title: { text: undefined },
    };
  }

  const appointmentConversionBarChartOptions = {
    chart: {
      type: 'bar',
      height: 100,
    },
    plotOptions: {
      series: {
        pointPadding: 0,
        borderWidth: 0,
      },
    },
    legend: {
      layout: 'vertical',
      align: 'left',
      verticalAlign: 'middle',
    },
    xAxis: {
      labels: { enabled: false },
      type: 'category',
    },
    yAxis: {
      title: { text: undefined },
      labels: {
        format: '{value}%',
      },
    },
    series: [
      {
        name: '6 Month Avg - Peers',
        data: [sixMonthAverageConversionIndustry],
        color: '#fbbf24',
      },
      {
        name: '6 Month Avg - You',
        data: [sixMonthAverageConversionYou],
        color: '#0ea5e9',
      },
    ],
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: { text: undefined },
    tooltip: {
      formatter() {
        return `${this.y}%`;
      },
    },
  };

  return (
    <div className='row'>
      <div className='col-sm-6 col-lg-3'>
        <PerformanceSummaryWidget
          title='Bookable Prospects'
          color='bookable_prospects'
          value={latestMonthData.bookable_calls ?? 0}
          delta={latestMonthData.bookable_calls_delta ?? 0}
          chart={
            <HighchartsReact
              highcharts={Highcharts}
              options={createSplineChartOptions(
                bookableProspectsStartData,
                bookableProspectsEndData
              )}
            />
          }
        />
      </div>
      <div className='col-sm-6 col-lg-3'>
        <PerformanceSummaryWidget
          title='Appointment Conversion'
          color='appointment'
          value={latestMonthData.appt_conversion_rate ?? 0}
          delta={latestMonthData.appt_conversion_rate_delta ?? 0}
          secondaryContent={
            <div style={{ border: '1px solid #d1d5db' }}>
              <HighchartsReact
                highcharts={Highcharts}
                options={appointmentConversionBarChartOptions}
              />
            </div>
          }
          chart={
            <HighchartsReact
              highcharts={Highcharts}
              options={createSplineChartOptions(
                appointmentConversionStartData,
                appointmentConversionEndData,
                true
              )}
            />
          }
          isPercentage={true}
        />
      </div>
      <div className='col-sm-6 col-lg-3'>
        <PerformanceSummaryWidget
          title='Appointments Set'
          color='appointment'
          value={latestMonthData.appt_calls ?? 0}
          delta={latestMonthData.appt_calls_delta ?? 0}
          chart={
            <HighchartsReact
              highcharts={Highcharts}
              options={createSplineChartOptions(appointmentsSetStartData, appointmentsSetEndData)}
            />
          }
        />
      </div>
      <div className='col-sm-6 col-lg-3'>
        <PerformanceSummaryWidget
          title='Missed Opportunities'
          color='opportunity'
          value={latestMonthData.noappt_calls ?? 0}
          secondaryContent={
            <div>
              <div className='missed_opportunity_value_text'>Missed Opportunity Value*</div>
              <div className='missed_opportunity_value_amount'>
                $
                {Common.formatNumberWithCommas(latestMonthData.noappt_calls * BLENDED_TICKET_VALUE)}
              </div>
              <div className='missed_opportunity_value_subtext'>{`*Blended ticket value $${Common.formatNumberWithCommas(
                BLENDED_TICKET_VALUE
              )}`}</div>
            </div>
          }
          chart={
            <HighchartsReact
              highcharts={Highcharts}
              options={createSplineChartOptions(
                missedOpportunitiesStartData,
                missedOpportunitiesEndData
              )}
            />
          }
          delta={latestMonthData.noappt_calls_delta ?? 0}
          invertChangeColor={true}
        />
      </div>
    </div>
  );
}

Dashboard.propTypes = {
  data: PropTypes.object.isRequired,
  sixMonthAverageConversionIndustry: PropTypes.number.isRequired,
  sixMonthAverageConversionYou: PropTypes.number.isRequired,
  currentYear: PropTypes.string.isRequired,
  previousYear: PropTypes.string.isRequired,
};

export default Dashboard;
